import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Card } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { UseFormRegister, UseFormWatch, UseFormSetValue, FieldErrors, UseFormTrigger } from 'react-hook-form';
import { ItemDetailsProps } from './item-details.types';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../hooks/useRedux';
import { calculateInvoice, getTotalCalculation } from '../features/invoice/invoice.slice';
import { setSelectedFreelancersForInvoice } from '../features/customer/customer.slice';
import { InvoiceModals } from './InvoiceModals';
import { FreelancerCardContainer } from './FreelancerCardContainer';
import DefaultInvoiceSection from './DefaultInvoiceSection';
import AutoGenerateItemsModal from './AutoGenerateItemsModal';
import ExpressPaymentSection from './ExpressPaymentSection';
import { Tooltip } from './common/tooltip.component';
import { useInvoiceCalculation } from '../hooks/useInvoiceCalculation.hook';
import { useFormInitialization } from '../hooks/useFormInitialization.hook';
import { useFreelancerCleanup } from '../hooks/useFreelancerCleanup.hook';
import { useExpressPaymentValidation } from '../hooks/useExpressPaymentValidation.hook';
import { useTableRowHandlers } from '../hooks/useTableRowHandlers.hook';
import { useDateSelection } from '../hooks/useDateSelection.hook';
import { useDeductionSubmit } from '../hooks/useDeductionSubmit.hook';
import { useCalculationBreakdown } from '../hooks/useCalculationBreakdown.hook';
import { useTableRowUpdates } from '../hooks/useTableRowUpdates.hook';
import { useTotalCalculation } from '../hooks/useTotalCalculation.hook';
import { useBenefitsManagement } from '../hooks/useBenefitsManagement.hook';
import { useFreelancerState } from '../hooks/useFreelancerState.hook';
import { useModalState } from '../hooks/useModalState.hook';
import { useDropdownState } from '../hooks/useDropdownState.hook';
import { useCustomerData } from '../hooks/useCustomerData.hook';
import { useWeekOffDaysFiltering } from '../hooks/useWeekOffDaysFiltering.hook';
import { useModalHandlers } from '../hooks/useModalHandlers.hook';
import { useInputChangeHandler } from '../hooks/useInputChangeHandler.hook';
import { useParentNotificationsAll } from '../hooks/useParentNotificationsAll.hook';
import { useTotalAmountCalculation } from '../hooks/useTotalAmountCalculation.hook';
import { useFreelancerExpansion } from '../hooks/useFreelancerExpansion.hook';
import { useDropdownOptions } from '../hooks/useDropdownOptions.hook';
import { usePricingType } from '../hooks/usePricingType.hook';
import { useEngagementType } from '../hooks/useEngagementType.hook';
import { createFieldValidator } from '../utils/fieldValidation.utils';
import { convertToDecimalHours, convertToTimeFormat } from '../utils/timeConversion.utils';
import { readCookie } from '../../common/FunctionUtils';
import './item-details.scss';

const ItemDetails: React.FC<ItemDetailsProps> = ({
  items = [],
  users = [],
  articles = [],
  currency = 'SEK',
  control,
  register,
  watch,
  setValue,
  errors,
  trigger,
  t: propT,
  globalData,
  onAddNewRow,
  onRemoveItem,
  showTooltipError = {},
  setShowTooltipError,
  preferences = {},
  calculatedAmounts,
  onChangeUnit,
  onChangeAmount,
  onServiceTableChange, // Add to destructured props
  onServiceTableDataByFreelancerChange, // NEW - Add to destructured props
  onPensionDataByFreelancerChange, // NEW - Add to destructured props
  onHolidayDataByFreelancerChange, // NEW - Add to destructured props
  onDeductionsByFreelancerChange, // NEW - Add to destructured props
  onRemovedCopiedWorkingHoursChange, // NEW - Add to destructured props
  onCalculatedSubAmountChange, // NEW - Add to destructured props
  copiedInvoiceData,  // NEW - Add to destructured props
  isEditModeInvoice,
  timesheetData,  // NEW - Add to destructured props
  preId,  // NEW - Add to destructured props
  validateServiceDescriptionsRef,
  validateDatesRef,
  createdDate,  // NEW - Add createdDate from URL
  invoiceNumber = 0, // NEW - Invoice number from URL for edit mode
  stepTitle,     // NEW - section header rendered inside the card
  stepSubtitle,  // NEW
  // formData,
}) => {
  // Get customer data using custom hook
  const { customerState, selectedCustomer, selectedFreelancersForInvoice } = useCustomerData();
  
  const authState = useAppSelector((state: any) => state?.auth || {});
  const { user } = authState;
  
  // Get BankID status from localStorage user object
  const localStorageUser = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user') || '{}') : null;
  const isLoginWithBankId = localStorageUser?.isLoginWithBankId ? 1 : 0;
  
 
  const dispatch = useAppDispatch();
  const invoiceState = useAppSelector((state: any) => state?.invoice || {});
  const { calculationData } = invoiceState;
  const { t, i18n } = useTranslation();

  // Phase 4: Consolidated state management hooks
  const freelancerState = useFreelancerState();
  const modalState = useModalState();
  const dropdownState = useDropdownState();

  // Destructure freelancer state for easier access
  const {
    tableRowsByFreelancer, setTableRowsByFreelancer,
    serviceTableDataByFreelancer, setServiceTableDataByFreelancer,
    pensionDataByFreelancer, setPensionDataByFreelancer,
    holidayDataByFreelancer, setHolidayDataByFreelancer,
    deductionsByFreelancer, setDeductionsByFreelancer,
    calendarSelectedDatesByFreelancer, setCalendarSelectedDatesByFreelancer,
    lastUsedPricingTypeByFreelancer, setLastUsedPricingTypeByFreelancer,
    expandedSections, setExpandedSections,
    removedCopiedWorkingHours, setRemovedCopiedWorkingHours
  } = freelancerState;

  // Destructure modal state
  const {
    showCalculationModal, setShowCalculationModal,
    calculationBreakdownData, setCalculationBreakdownData,
    isCalculationLoading, setIsCalculationLoading,
    showExpressPaymentModal, setShowExpressPaymentModal,
    showExpressValidMessage, setShowExpressValidMessage,
    isExpressCheckboxChecked, setIsExpressCheckboxChecked,
    showCalendar, setShowCalendar,
    activeFreelancerForCalendar, setActiveFreelancerForCalendar,
    showDeductionModal, setShowDeductionModal,
    currentDeductionFreelancerId, setCurrentDeductionFreelancerId,
    editingDeductionIndex, setEditingDeductionIndex,
    editingDeductionData, setEditingDeductionData,
    editingCopiedWorkingHourIndex, setEditingCopiedWorkingHourIndex,
    pensionPay, setPensionPay,
    holidayPay, setHolidayPay,
    expressPayments, setExpressPayments,
    calculatedSubtotal, setCalculatedSubtotal,
    calculatedTotal, setCalculatedTotal,
    vatAmount, setVatAmount
  } = modalState;

  // Destructure dropdown state
  const {
    isShowDatesDropdownOpen, setIsShowDatesDropdownOpen, showDatesDropdownRef,
    isHoursPerDayDropdownOpen, setIsHoursPerDayDropdownOpen, hoursPerDayDropdownRef,
    isWeekOffDaysDropdownOpen, setIsWeekOffDaysDropdownOpen, weekOffDaysDropdownRef
  } = dropdownState;

  // Additional UI state
  const [weekOffDays, setWeekOffDays] = useState<string[]>([]);
  const [serviceDescription, setServiceDescription] = useState<string>('');
  const [serviceDescriptionError, setServiceDescriptionError] = useState<string>('');

  // Deduplicate timesheetData at the top level to prevent duplicate working hours in payload
  const deduplicatedTimesheetData = useMemo(() => {
    if (!timesheetData || timesheetData.length === 0) {
      return [];
    }
    
    const deduplicated = timesheetData.reduce((acc: any[], current: any) => {
      // Check if this item already exists in accumulator
      const isDuplicate = acc.some((item: any) => {
        // Priority 1: If timeSheetWorkingHourId exists and is not 0, use it as PRIMARY unique identifier
        // Same timeSheetWorkingHourId = duplicate, remove it
        if (current.timeSheetWorkingHourId && current.timeSheetWorkingHourId !== 0 && 
            item.timeSheetWorkingHourId && item.timeSheetWorkingHourId !== 0) {
          return item.timeSheetWorkingHourId === current.timeSheetWorkingHourId;
        }
        // Priority 2: If no valid timeSheetWorkingHourId, check by combination
        // Different timeSheetWorkingHourId with same date is VALID (don't remove)
        // Only deduplicate if ALL fields match (userId, workingDate, workingHour, articleNumber)
        if (!current.timeSheetWorkingHourId || current.timeSheetWorkingHourId === 0) {
          return (
            item.userId === current.userId &&
            item.workingDate === current.workingDate &&
            item.workingHour === current.workingHour &&
            item.articleNumber === current.articleNumber
          );
        }
        return false;
      });
      
      if (!isDuplicate) {
        acc.push(current);
      }
      return acc;
    }, []);
    
    return deduplicated;
  }, [timesheetData]);

  // Filter unique freelancers to prevent duplicate rendering when same userId appears multiple times
  const uniqueFreelancersForInvoice = useMemo(() => {
    // console.log('🔍 [FREELANCER TRACE] Raw selectedFreelancersForInvoice from Redux:', selectedFreelancersForInvoice);
    // console.log('🔍 [FREELANCER TRACE] Length:', selectedFreelancersForInvoice?.length);
    
    if (!selectedFreelancersForInvoice || selectedFreelancersForInvoice.length === 0) {
      // console.log('⚠️ [FREELANCER TRACE] No freelancers found, returning empty array');
      return [];
    }
    
    const seen = new Set<string>();
    const uniqueList: any[] = [];
    
    selectedFreelancersForInvoice.forEach((freelancer: any) => {
      const freelancerUserId = (freelancer.userId || freelancer.id)?.toString();
      
      if (freelancerUserId && !seen.has(freelancerUserId)) {
        seen.add(freelancerUserId);
        uniqueList.push(freelancer);
      }
    });
    
    // If timesheetData is available and URL has timesheet=true parameter, 
    // filter to only show freelancers who have data in timesheetData
    const searchParams = new URLSearchParams(window.location.search);
    const timesheetParam = searchParams.get('timesheet');
    
    if (timesheetParam === 'true' && deduplicatedTimesheetData && deduplicatedTimesheetData.length > 0) {
      // Get unique userIds from timesheetData
      const userIdsInTimesheet = new Set(
        deduplicatedTimesheetData.map((item: any) => item.userId?.toString()).filter(Boolean)
      );
      
      // Filter to only include freelancers that have timesheet data
      const filteredList = uniqueList.filter((freelancer: any) => {
        const freelancerUserId = (freelancer.userId || freelancer.id)?.toString();
        return userIdsInTimesheet.has(freelancerUserId);
      });
      
      // console.log('✅ [FREELANCER TRACE] Filtered list for timesheet mode:', filteredList);
      return filteredList;
    }
    
    // console.log('✅ [FREELANCER TRACE] Final unique freelancers list:', uniqueList);
    return uniqueList;
  }, [selectedFreelancersForInvoice, deduplicatedTimesheetData]);

  useFreelancerExpansion({
    selectedFreelancersForInvoice: uniqueFreelancersForInvoice,
    expandedSections,
    setExpandedSections,
  });

  // Note: Form initialization logic has been moved to useFormInitialization hook
  useFormInitialization({
    copiedInvoiceData,
    setValue,
    watch,
    setTableRowsByFreelancer,
    setCalendarSelectedDatesByFreelancer,
    setPensionDataByFreelancer,
    setHolidayDataByFreelancer,
    setPensionPay,
    setHolidayPay,
    setExpressPayments,
  });

  // Note: Freelancer cleanup logic has been moved to useFreelancerCleanup hook
  useFreelancerCleanup({
    selectedFreelancersForInvoice,
    copiedInvoiceData,
    setTableRowsByFreelancer,
    setServiceTableDataByFreelancer,
    setDeductionsByFreelancer,
    setPensionDataByFreelancer,
    setHolidayDataByFreelancer,
    setCalendarSelectedDatesByFreelancer,
    setLastUsedPricingTypeByFreelancer,
    setExpandedSections,
    setRemovedCopiedWorkingHours,
  });

  // Refs for tracking state changes and preventing race conditions (initialized before use)
  const isInitialMount = React.useRef(true);
  const latestRequestIdRef = React.useRef<number>(0);
  const prevServiceDataRef = React.useRef<string>('');
  const prevCurrencyRef = React.useRef<string>(currency);
  const prevPensionDataRef = React.useRef<string>(JSON.stringify(pensionDataByFreelancer));
  const prevHolidayDataRef = React.useRef<string>(JSON.stringify(holidayDataByFreelancer));
  const prevDeductionsDataRef = React.useRef<string>(JSON.stringify(deductionsByFreelancer));

  useTotalAmountCalculation({ tableRowsByFreelancer, setValue });

  const { pricingType, isHourlyPrice, isDailyPrice, isWeeklyPrice, isMonthlyPrice, isAgreementPrice } = usePricingType({ preferences });
  const { engagementType, isRegularEngagement, isOccasionalEngagement } = useEngagementType({ preferences });

  // Clear week-off days when engagement type changes to occasional
  useEffect(() => {
    if (isOccasionalEngagement) {
      setWeekOffDays([]);
    }
  }, [isOccasionalEngagement]);

  // Re-filter dates when week-off days change - handled by custom hook
  useWeekOffDaysFiltering({
    weekOffDays,
    isRegularEngagement,
    setTableRowsByFreelancer,
    calendarSelectedDatesByFreelancer,
    setCalendarSelectedDatesByFreelancer,
    setValue
  });

  const accommodation = watch('accommodation');
  const foodAllowance = watch('foodAllowance');
  const mileage = watch('mileage');
  const workDates = watch('workDates');
  const showDates = watch('showDates');
  const deductionType = preferences?.deductionType || 'none';
  const isRutOrRotDeduction = deductionType === 'rut' || deductionType === 'rot';
  const vat = typeof preferences?.vat === 'number' ? preferences.vat : (typeof preferences?.vat === 'string' ? parseInt(preferences.vat) : 25);
  const hoursPerDayType = watch('hoursPerDayType');
  const pensionValue = watch('pension');
  const holidaySavings = watch('holidaySavings');
  const expressPayout = watch('expressPayout');
  const workTime = watch('workTime');
  const hourlyPrice = watch('hourlyPrice');

  const prevHourlyPriceRef = React.useRef<string>(hourlyPrice || '');
  const prevVatRef = React.useRef<number>(vat);
  const prevDeductionTypeRef = React.useRef<string>(deductionType);

  const isInitialMountRef = useRef(true);
  
  // Store modal data per freelancer to avoid cross-contamination
  const [modalDataByFreelancer, setModalDataByFreelancer] = useState<Record<string, any>>({});
  const { 
    calculatedSubAmount, 
    isCalculateInvoiceLoading,
    setCalculatedSubAmount 
  } = useInvoiceCalculation({
    serviceTableDataByFreelancer,
    currency,
    deductionType,
    vat,
    hourlyPrice,
    selectedFreelancersForInvoice,
    pensionDataByFreelancer,
    holidayDataByFreelancer,
    deductionsByFreelancer,
    watch,
    calculatedAmounts,
    preferences,
    customerState,
    globalData,
    copiedInvoiceData,
    removedCopiedWorkingHours,
    isInitialMount: isInitialMountRef.current,
    createdDate,
    invoiceNumber // Pass invoiceNumber to the hook
  });

  // Calculate VAT amount from the invoice calculation response
  useEffect(() => {
    if (calculatedSubAmount?.response?.totalCalculation) {
      const amount = calculatedSubAmount.response.totalCalculation.amount || 0;
      const amountIncludingVat = calculatedSubAmount.response.totalCalculation.amountIncludingVat || 0;
      const calculatedVat = amountIncludingVat - amount;
      setVatAmount(calculatedVat);
    } else {
      setVatAmount(0);
    }
  }, [calculatedSubAmount]);

  useParentNotificationsAll({
    serviceTableDataByFreelancer,
    pensionDataByFreelancer,
    holidayDataByFreelancer,
    deductionsByFreelancer,
    removedCopiedWorkingHours,
    calculatedSubAmount,
    onServiceTableDataByFreelancerChange,
    onPensionDataByFreelancerChange,
    onHolidayDataByFreelancerChange,
    onDeductionsByFreelancerChange,
    onRemovedCopiedWorkingHoursChange,
    onCalculatedSubAmountChange,
  });

  useEffect(() => {
    isInitialMountRef.current = false;
  }, []);

  const { accommodationOptions, allowanceOptions, showDatesOptions, hoursPerDayOptions, serviceOptions } = useDropdownOptions({ t, articles });

  const { validateFieldWithTooltip, validateArticleWithPricingTypeAndTooltip, clearValidationErrors } = 
    createFieldValidator(setShowTooltipError || (() => {}));

  const { handleInputChange } = useInputChangeHandler({
    setValue,
    setShowTooltipError,
    tableRowsByFreelancer,
    setTableRowsByFreelancer,
    isHourlyPrice,
    isDailyPrice,
    isWeeklyPrice,
    isMonthlyPrice,
    isAgreementPrice,
    validateArticleWithPricingTypeAndTooltip,
    clearValidationErrors,
    showTooltipError,
  });

  // How the Express Payment requirements modal was opened:
  //  'info'    → from "Read more" (informational, no action buttons)
  //  'confirm' → from the toggle (Accept / Reject)
  const [expressModalMode, setExpressModalMode] = useState<'info' | 'confirm'>('confirm');

  // Note: Express Payment validation logic has been moved to useExpressPaymentValidation hook
  const {
    language,
    expressDe,
    expressEng,
    getExpressPaymentMessage,
    isExpressPaymentAvailable,
    shouldShowWarningMessage,
    handleExpressPaymentCheckbox,
    handleExpressPaymentReadMore,
    handleExpressPaymentModalConfirm,
    handleExpressPaymentModalReject,
    handleExpressPaymentUncheckRequest,
  } = useExpressPaymentValidation({
    isLoginWithBankId,
    selectedCustomer,
    calculatedSubAmount,
    t,
    setValue,
    setIsExpressCheckboxChecked,
    setShowExpressPaymentModal,
    setShowExpressValidMessage,
    setExpressPayments,
    handleInputChange,
    setExpressModalMode,
  });

  // Phase 1: Table row handlers hook
  const { handleRowChange, handleRemoveRow, handleAddRow, handleAddWorkingHoursSection } = useTableRowHandlers({
    tableRowsByFreelancer,
    setTableRowsByFreelancer,
    lastUsedPricingTypeByFreelancer,
    setLastUsedPricingTypeByFreelancer,
    calendarSelectedDatesByFreelancer,
    setCalendarSelectedDatesByFreelancer,
    watch,
    hourlyPrice,
    isRutOrRotDeduction,
    serviceDescription
  });

  // Phase 1: Date selection hook
  const { handleDateSelect } = useDateSelection({
    setCalendarSelectedDatesByFreelancer,
    setTableRowsByFreelancer,
    setShowCalendar,
    lastUsedPricingTypeByFreelancer,
    isRegularEngagement,
    weekOffDays,
    isRutOrRotDeduction,
    serviceDescription,
    setValue,
    handleInputChange,
    watch
  });

  // Auto-seed a default working-hour row with TODAY's date for each freelancer, so
  // the user starts with a ready row instead of the empty "Select Date" placeholder.
  // Only for fresh creation (never edit/copy/draft/timesheet — those load their own
  // rows), only when a customer is selected, and only once per freelancer (so
  // deleting the last row doesn't immediately re-add one).
  const autoSeededRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const spTimesheet = new URLSearchParams(window.location.search).get('timesheet') === 'true';
    if (isEditModeInvoice || copiedInvoiceData || preId || spTimesheet || (timesheetData && timesheetData.length > 0)) return;
    const hasCustomer = !!(
      selectedCustomer?.customerId || selectedCustomer?.id ||
      customerState?.selectedCustomer?.customerId || customerState?.selectedCustomer?.id
    );
    if (!hasCustomer) return;

    const today = new Date();
    const currentDate = `${String(today.getMonth() + 1).padStart(2, '0')}/${String(today.getDate()).padStart(2, '0')}/${today.getFullYear()}`;
    const uid = () => (typeof crypto !== 'undefined' && (crypto as any).randomUUID ? (crypto as any).randomUUID() : `${Date.now()}${Math.random().toString(36).slice(2)}`);

    const ids: string[] = (selectedFreelancersForInvoice && selectedFreelancersForInvoice.length > 0)
      ? selectedFreelancersForInvoice.map((f: any) => (f.userId?.toString() || f.id?.toString()))
      : ['default'];

    ids.forEach((id: string) => {
      if (!id || autoSeededRef.current.has(id)) return;
      const rows = tableRowsByFreelancer[id] || [];
      // Mark as seeded first so the state update this triggers can't re-enter.
      autoSeededRef.current.add(id);
      if (rows.length > 0) return; // already has rows (e.g. from init) — don't duplicate
      handleAddWorkingHoursSection(id, {
        id: uid(), workingHourId: 0, timeSheetWorkingHourId: 0,
        date: currentDate, pricingType: 'Tim', pricingTypeArticleNumber: '92',
        service: '', customWorkingDescription: '', time: '08:00', price: '500',
        discount: { value: 0, type: 'percentage' as const }, subtotal: 0,
      });
    });
  }, [selectedCustomer, customerState, selectedFreelancersForInvoice, tableRowsByFreelancer, isEditModeInvoice, copiedInvoiceData, preId, timesheetData, handleAddWorkingHoursSection]);

  // Get stored modal data for a freelancer
  const getModalDataByFreelancer = (freelancerId: string) => {
    const data = modalDataByFreelancer[freelancerId];
    return data ? {
      formData: data.formData,
      selectedDates: data.selectedDates
    } : undefined;
  };

  // Extract dates from tableRowsByFreelancer and populate modalDataByFreelancer when fetching data
  // This works for both: 1) copied invoice data, 2) timesheet data loaded into working hours
  useEffect(() => {
    if (tableRowsByFreelancer && Object.keys(tableRowsByFreelancer).length > 0) {
      const newModalData: Record<string, any> = {};
      
      Object.keys(tableRowsByFreelancer).forEach(freelancerId => {
        const rows = tableRowsByFreelancer[freelancerId];
        if (rows && rows.length > 0) {
          // Extract dates from rows - date is in format "MM/DD/YYYY" or "YYYY-MM-DD"
          const extractedDates = rows
            .filter((row: any) => row.date)
            .map((row: any) => {
              // Parse date string to Date object
              const dateStr = row.date;
              let parsedDate: Date;
              
              // Check if date is in MM/DD/YYYY format
              if (dateStr.includes('/')) {
                const [month, day, year] = dateStr.split('/');
                parsedDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
              } else {
                // Assume YYYY-MM-DD format
                parsedDate = new Date(dateStr);
              }
              
              return parsedDate;
            })
            .filter((date: Date) => !isNaN(date.getTime())); // Filter out invalid dates
          
          // Extract form data from first row
          const firstRow = rows[0];
          if (extractedDates.length > 0 && firstRow) {
            newModalData[freelancerId] = {
              formData: {
                hoursPerDay: firstRow.time || '08:00',
                hourlyPrice: firstRow.price?.toString() || '',
                showDates: 'yes',
                weekOffDays: [],
                serviceDescription: firstRow.service || firstRow.customWorkingDescription || '',
                pricingType: firstRow.pricingType || 'Tim',
              },
              selectedDates: extractedDates
            };
          }
        }
      });
      
      // Only update if we found data and modalDataByFreelancer is empty
      if (Object.keys(newModalData).length > 0 && Object.keys(modalDataByFreelancer).length === 0) {
        setModalDataByFreelancer(newModalData);
      }
    }
  }, [tableRowsByFreelancer]);

  // Sync modal dates with table rows whenever rows change (add/remove)
  useEffect(() => {
    if (tableRowsByFreelancer && Object.keys(tableRowsByFreelancer).length > 0) {
      setModalDataByFreelancer(prev => {
        const updated = { ...prev };
        
        Object.keys(tableRowsByFreelancer).forEach(freelancerId => {
          const rows = tableRowsByFreelancer[freelancerId];
          
          // Extract dates from current rows
          const extractedDates = rows
            .filter((row: any) => row.date)
            .map((row: any) => {
              const dateStr = row.date;
              let parsedDate: Date;
              
              if (dateStr.includes('/')) {
                const [month, day, year] = dateStr.split('/');
                parsedDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
              } else {
                parsedDate = new Date(dateStr);
              }
              
              return parsedDate;
            })
            .filter((date: Date) => !isNaN(date.getTime()));
          
          // If this freelancer has existing modal data, update only the dates
          if (prev[freelancerId]) {
            updated[freelancerId] = {
              ...prev[freelancerId],
              selectedDates: extractedDates
            };
          }
        });
        
        return updated;
      });
    }
  }, [tableRowsByFreelancer]);

  // Handler for auto-generate modal
  const handleAutoGenerateItems = (freelancerId: string, modalData: any, selectedDates: Date[]) => {
    // Store modal data per freelancer for reference
    setModalDataByFreelancer(prev => ({
      ...prev,
      [freelancerId]: {
        formData: modalData,
        selectedDates: selectedDates
      }
    }));
    
    // Generate table rows using handleDateSelect with freelancer-specific overrides
    // This prevents data from one freelancer affecting another
    if (selectedDates.length > 0) {
      handleDateSelect(
        freelancerId,
        selectedDates[0],
        selectedDates[selectedDates.length - 1],
        selectedDates,
        {
          hoursPerDay: modalData.hoursPerDay,
          hourlyPrice: modalData.hourlyPrice,
          serviceDescription: modalData.serviceDescription,
          weekOffDays: modalData.weekOffDays,
          pricingType: modalData.pricingType,
          // Preserve any previously added working hours for this freelancer;
          // only the newly picked dates should be appended.
          appendToExisting: true
        }
      );
    }
  };

  // Handler for removing freelancer from invoice
  const handleRemoveFreelancer = (freelancerId: string) => {
    // Prevent removal of the freelancer at index 0
    if (selectedFreelancersForInvoice.length > 0 && freelancerId === (selectedFreelancersForInvoice[0].userId?.toString() || selectedFreelancersForInvoice[0].id?.toString())) {
      return;
    }
    // Remove freelancer from the selected list
    const updatedFreelancers = selectedFreelancersForInvoice.filter(
      (f: any) => (f.userId?.toString() || f.id?.toString()) !== freelancerId
    );
    dispatch(setSelectedFreelancersForInvoice(updatedFreelancers));
    
    // Clean up all related data for this freelancer
    setTableRowsByFreelancer(prev => {
      const updated = { ...prev };
      delete updated[freelancerId];
      return updated;
    });
    setServiceTableDataByFreelancer(prev => {
      const updated = { ...prev };
      delete updated[freelancerId];
      return updated;
    });
    setDeductionsByFreelancer(prev => {
      const updated = { ...prev };
      delete updated[freelancerId];
      return updated;
    });
    setPensionDataByFreelancer(prev => {
      const updated = { ...prev };
      delete updated[freelancerId];
      return updated;
    });
    setHolidayDataByFreelancer(prev => {
      const updated = { ...prev };
      delete updated[freelancerId];
      return updated;
    });
    setCalendarSelectedDatesByFreelancer(prev => {
      const updated = { ...prev };
      delete updated[freelancerId];
      return updated;
    });
    setModalDataByFreelancer(prev => {
      const updated = { ...prev };
      delete updated[freelancerId];
      return updated;
    });
  };

  // Phase 1: Deduction submit hook
  const { handleDeductionSubmit } = useDeductionSubmit({
    currentDeductionFreelancerId,
    editingDeductionIndex,
    deductionsByFreelancer,
    setDeductionsByFreelancer,
    serviceTableDataByFreelancer,
    pensionDataByFreelancer,
    holidayDataByFreelancer,
    editingCopiedWorkingHourIndex,
    setEditingCopiedWorkingHourIndex,
    setRemovedCopiedWorkingHours,
    selectedFreelancersForInvoice,
    calculatedAmounts,
    copiedInvoiceData,
    removedCopiedWorkingHours,
    latestRequestIdRef,
    setCalculatedSubAmount,
    preferences,
    vat,
    currency,
    globalData,
    deductionType,
    customerState,
    watch,
    dispatch,
    createdDate,
    invoiceNumber // Pass invoiceNumber to the hook
  });

  // Phase 1: Calculation breakdown hook
  const { handleShowCalculationBreakdown } = useCalculationBreakdown({
    setIsCalculationLoading,
    setCalculationBreakdownData,
    setShowCalculationModal,
    selectedFreelancersForInvoice,
    serviceTableDataByFreelancer,
    deductionsByFreelancer,
    pensionDataByFreelancer,
    holidayDataByFreelancer,
    copiedInvoiceData,
    removedCopiedWorkingHours,
    calculatedAmounts,
    preferences,
    globalData,
    customerState,
    watch,
    dispatch,
    createdDate
  });

  // Phase 2: Effect hooks for table row updates and calculations
  useTableRowUpdates({
    hoursPerDayType: watch('hoursPerDayType') || '',
    hourlyPrice,
    serviceDescription,
    deductionType,
    tableRowsByFreelancer,
    setTableRowsByFreelancer,
    convertToDecimalHours,
    convertToTimeFormat
  });

  useBenefitsManagement({
    isOccasionalEngagement,
    engagementType,
    pensionValue,
    holidaySavings,
    setValue,
    setPensionPay,
    setHolidayPay
  });

  const { handleCalendarOpen, handleCalendarClose, handleOpenDeductionModal, handleCloseDeductionModal, toggleSection } = useModalHandlers({
    setActiveFreelancerForCalendar,
    setShowCalendar,
    setCurrentDeductionFreelancerId,
    setEditingDeductionIndex,
    setEditingDeductionData,
    setShowDeductionModal,
    setEditingCopiedWorkingHourIndex,
    deductionsByFreelancer,
    setExpandedSections,
    setValue,
    setShowTooltipError,
  });

  // handleRowChange, handleRemoveRow, handleAddRow, handleAddWorkingHoursSection
  // are now provided by useTableRowHandlers hook

  // The Express Payment UI is displayed in the wizard's Step 5 (Payment & Summary)
  // via a portal, while all of its logic/validation/modals stay here in Items.
  const [expressSlot, setExpressSlot] = React.useState<HTMLElement | null>(null);
  React.useEffect(() => {
    let raf = 0;
    const findSlot = () => {
      const el = document.getElementById('iw-express-slot');
      if (el) { setExpressSlot(el); } else { raf = requestAnimationFrame(findSlot); }
    };
    findSlot();
    return () => { if (raf) cancelAnimationFrame(raf); };
  }, []);

  // Freelancer tabs act as a VISIBILITY filter (UI only). Hidden freelancers
  // stay mounted, so ALL their data/amounts remain in state and in the payload.
  const [activeFreelancerTab, setActiveFreelancerTab] = React.useState<string | null>(null);
  const firstFreelancerUid = ((uniqueFreelancersForInvoice[0]?.userId ?? uniqueFreelancersForInvoice[0]?.id))?.toString() || null;
  // Default = the logged-in user (index 0); user can switch tabs or pick "All"
  const effectiveFreelancerTab = activeFreelancerTab || firstFreelancerUid || 'all';
  // Open the current user's card by default the first time
  React.useEffect(() => {
    if (firstFreelancerUid) {
      setExpandedSections((prev: any) => (prev && prev[firstFreelancerUid] ? prev : { ...(prev || {}), [firstFreelancerUid]: true }));
    }
  }, [firstFreelancerUid]);
  const handleFreelancerTabClick = (uid: string) => {
    setActiveFreelancerTab(uid);
    setExpandedSections((prev: any) => ({ ...(prev || {}), [uid]: true }));
  };

  // Remove-from-tab: open the same confirmation, then remove + redirect the active
  // tab to the previous freelancer (never touches other freelancers' data).
  const [removeTabTarget, setRemoveTabTarget] = React.useState<{ uid: string; name: string } | null>(null);
  // Global "Add multiple rows" (with freelancer selector) opened from the tabs bar
  const [showGlobalAutoGen, setShowGlobalAutoGen] = React.useState(false);
  const freelancerTargetOptions = React.useMemo(() => ([
    { value: 'all', label: (t('ALL') as string) || 'All' },
    ...uniqueFreelancersForInvoice.map((f: any) => ({
      value: (f.userId || f.id)?.toString(),
      label: f.name || f.userName || f.email || 'Freelancer',
    })),
  ]), [uniqueFreelancersForInvoice, t]);
  // Dates each freelancer ALREADY has working hours on — shown as read-only
  // "already added" badges in the global modal so the user avoids double-booking.
  const freelancerExistingDates = React.useMemo(() => {
    const map: { [uid: string]: { name: string; dates: string[] } } = {};
    uniqueFreelancersForInvoice.forEach((f: any) => {
      const uid = (f.userId || f.id)?.toString();
      if (!uid) return;
      const rows = tableRowsByFreelancer[uid] || [];
      const dates = rows.map((r: any) => r.date).filter((d: string) => d && d.trim() !== '');
      map[uid] = { name: f.name || f.userName || f.email || 'Freelancer', dates };
    });
    return map;
  }, [uniqueFreelancersForInvoice, tableRowsByFreelancer]);

  // Remove every working-hour row for a given freelancer that matches a date (from the "already added" badge ×)
  const handleRemoveFreelancerDate = (uid: string, date: string) => {
    if (!uid || !date) return;
    const rows = tableRowsByFreelancer[uid] || [];
    rows
      .filter((r: any) => r.date === date)
      .forEach((r: any) => handleRemoveRow(uid, r.id));
  };

  const handleGlobalGenerate = (target: string, modalData: any, dates: Date[]) => {
    if (target === 'all') {
      const allUids = uniqueFreelancersForInvoice
        .map((f: any) => (f.userId || f.id)?.toString())
        .filter(Boolean);
      allUids.forEach((uid: string) => handleAutoGenerateItems(uid, modalData, dates));
      // Show every freelancer and expand all their cards (rows were added to each)
      setActiveFreelancerTab('all');
      setExpandedSections((prev: any) => {
        const next = { ...(prev || {}) };
        allUids.forEach((uid: string) => { next[uid] = true; });
        return next;
      });
    } else {
      handleAutoGenerateItems(target, modalData, dates);
      // Keep the freelancer we added rows for open + selected
      setActiveFreelancerTab(target);
      setExpandedSections((prev: any) => ({ ...(prev || {}), [target]: true }));
    }
  };
  const confirmRemoveTab = () => {
    if (!removeTabTarget) return;
    const uid = removeTabTarget.uid;
    if (effectiveFreelancerTab === uid) {
      const idx = uniqueFreelancersForInvoice.findIndex((f: any) => (f.userId || f.id)?.toString() === uid);
      const prev = idx > 0 ? uniqueFreelancersForInvoice[idx - 1] : uniqueFreelancersForInvoice[0];
      const prevUid = prev ? (prev.userId || prev.id)?.toString() : null;
      setActiveFreelancerTab(prevUid);
    }
    handleRemoveFreelancer(uid);
    setRemoveTabTarget(null);
  };

  return (
    <Card className="invoice-card mb-4">
      <Card.Header className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Card.Title as="h3">{t('ITEM_DETAILS')}</Card.Title>
        {(!selectedFreelancersForInvoice || selectedFreelancersForInvoice.length === 0) && (
          <Tooltip content={t('SELECT_CUSTOMER_FIRST_TOOLTIP')} position="bottom">
            <button
              disabled
              style={{
                padding: '6px 12px',
                fontSize: '14px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                background: '#f3f4f6',
                color: '#9ca3af',
                cursor: 'not-allowed',
                opacity: 0.6
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M7.69053 1.39112C7.80522 1.11963 8.18996 1.11963 8.30465 1.39112L10.1228 5.69503C10.1566 5.77497 10.2202 5.8386 10.3002 5.87237L14.6041 7.69053C14.8756 7.80522 14.8756 8.18996 14.6041 8.30465L10.3002 10.1228C10.2202 10.1566 10.1566 10.2202 10.1228 10.3002L8.30465 14.6041C8.18996 14.8756 7.80522 14.8756 7.69053 14.6041L5.87237 10.3002C5.8386 10.2202 5.77497 10.1566 5.69503 10.1228L1.39112 8.30465C1.11963 8.18996 1.11963 7.80522 1.39112 7.69053L5.69503 5.87237C5.77497 5.8386 5.8386 5.77497 5.87237 5.69503L7.69053 1.39112Z" stroke="#9ca3af" strokeWidth="2"/>
              </svg>
              {t('AUTO_GENERATE_ITEMS') || 'Auto Generate Items'}
            </button>
          </Tooltip>
        )}
      </Card.Header>
      <Card.Body className="card-body">
        {(stepTitle || stepSubtitle) && (
          <div className="cust-step-header iw-items-inhead">
            <div className="cust-step-heading iw-head-with-icon">
              <span className="iw-head-icon" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M8 7h8M8 11h8M8 15h5" /></svg>
              </span>
              <div className="iw-head-text">
                {stepTitle && <h2 className="cust-step-title">{stepTitle}</h2>}
                {stepSubtitle && <p className="cust-step-sub">{stepSubtitle}</p>}
              </div>
            </div>
          </div>
        )}
        <div className="item-details-container">

          {/* Display message if no freelancers are selected */}
          {!selectedFreelancersForInvoice || selectedFreelancersForInvoice.length === 0 ? (
            <DefaultInvoiceSection
              register={register}
              setValue={setValue}
              watch={watch}
              errors={errors}
              trigger={trigger}
              isRegularEngagement={isRegularEngagement}
              currency={currency}
              isHourlyPrice={isHourlyPrice}
              isDailyPrice={isDailyPrice}
              isWeeklyPrice={isWeeklyPrice}
              isMonthlyPrice={isMonthlyPrice}
              deductionType={deductionType}
              hoursPerDayType={hoursPerDayType}
              workDates={workDates}
              showDates={showDates}
              selectedCustomer={selectedCustomer}
              isHoursPerDayDropdownOpen={isHoursPerDayDropdownOpen}
              isShowDatesDropdownOpen={isShowDatesDropdownOpen}
              isWeekOffDaysDropdownOpen={isWeekOffDaysDropdownOpen}
              showDatesDropdownRef={showDatesDropdownRef}
              weekOffDaysDropdownRef={weekOffDaysDropdownRef}
              weekOffDays={weekOffDays}
              setWeekOffDays={setWeekOffDays}
              showCalendar={showCalendar}
              activeFreelancerForCalendar={activeFreelancerForCalendar}
              calendarSelectedDatesByFreelancer={calendarSelectedDatesByFreelancer}
              tableRowsByFreelancer={tableRowsByFreelancer}
              serviceTableDataByFreelancer={serviceTableDataByFreelancer}
              deductionsByFreelancer={deductionsByFreelancer}
              pensionDataByFreelancer={pensionDataByFreelancer}
              holidayDataByFreelancer={holidayDataByFreelancer}
              copiedInvoiceData={copiedInvoiceData}
              removedCopiedWorkingHours={removedCopiedWorkingHours}
              hoursPerDayOptions={hoursPerDayOptions}
              showDatesOptions={showDatesOptions}
              serviceOptions={serviceOptions}
              showTooltipError={showTooltipError}
              handleInputChange={handleInputChange}
              handleCalendarOpen={handleCalendarOpen}
              handleCalendarClose={handleCalendarClose}
              handleDateSelect={handleDateSelect}
              setIsHoursPerDayDropdownOpen={setIsHoursPerDayDropdownOpen}
              setIsShowDatesDropdownOpen={setIsShowDatesDropdownOpen}
              setIsWeekOffDaysDropdownOpen={setIsWeekOffDaysDropdownOpen}
              handleRowChange={handleRowChange}
              handleRemoveRow={handleRemoveRow}
              handleAddWorkingHoursSection={handleAddWorkingHoursSection}
              setServiceTableDataByFreelancer={setServiceTableDataByFreelancer}
              handleOpenDeductionModal={handleOpenDeductionModal}
              setEditingDeductionData={setEditingDeductionData}
              setCurrentDeductionFreelancerId={setCurrentDeductionFreelancerId}
              setEditingDeductionIndex={setEditingDeductionIndex}
              setShowDeductionModal={setShowDeductionModal}
              setRemovedCopiedWorkingHours={setRemovedCopiedWorkingHours}
              setDeductionsByFreelancer={setDeductionsByFreelancer}
              setPensionDataByFreelancer={setPensionDataByFreelancer}
              setHolidayDataByFreelancer={setHolidayDataByFreelancer}
              globalData={globalData}
              preferences={preferences}
              validateServiceDescriptionsRef={validateServiceDescriptionsRef}
              validateDatesRef={validateDatesRef}
              t={t}
              onAutoGenerateItems={handleAutoGenerateItems}
              getModalDataByFreelancer={getModalDataByFreelancer}
            />
          ) : (
            <>
            {/* Freelancer tabs — filter which freelancer card is shown ("All" = every card) */}
            {uniqueFreelancersForInvoice.length > 1 && (
              <div className="fc-tabs" role="tablist" aria-label="Freelancers">
                <button
                  type="button"
                  role="tab"
                  aria-selected={effectiveFreelancerTab === 'all'}
                  className={`fc-tab fc-tab--all${effectiveFreelancerTab === 'all' ? ' is-active' : ''}`}
                  onClick={() => setActiveFreelancerTab('all')}
                >
                  <span className="fc-tab-avatar fc-tab-avatar--all" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="3" width="8" height="8" rx="2" stroke="currentColor" strokeWidth="2" />
                      <rect x="13" y="3" width="8" height="8" rx="2" stroke="currentColor" strokeWidth="2" />
                      <rect x="3" y="13" width="8" height="8" rx="2" stroke="currentColor" strokeWidth="2" />
                      <rect x="13" y="13" width="8" height="8" rx="2" stroke="currentColor" strokeWidth="2" />
                    </svg>
                  </span>
                  <span className="fc-tab-name">{t('ALL') || 'All'}</span>
                </button>
                {uniqueFreelancersForInvoice.map((f: any, fi: number) => {
                  const uid = (f.userId || f.id)?.toString();
                  const fname = f.name || f.userName || f.email || 'Freelancer';
                  return (
                    <div
                      key={uid}
                      role="tab"
                      tabIndex={0}
                      aria-selected={effectiveFreelancerTab === uid}
                      className={`fc-tab${effectiveFreelancerTab === uid ? ' is-active' : ''}`}
                      onClick={() => handleFreelancerTabClick(uid)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleFreelancerTabClick(uid); } }}
                    >
                      <span className="fc-tab-avatar">{(fname || '?').charAt(0).toUpperCase()}</span>
                      <span className="fc-tab-name">{fname}</span>
                      {fi !== 0 && (
                        <span
                          className="fc-tab-x"
                          role="button"
                          tabIndex={0}
                          aria-label={`Remove ${fname}`}
                          onClick={(e) => { e.stopPropagation(); setRemoveTabTarget({ uid, name: fname }); }}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); e.preventDefault(); setRemoveTabTarget({ uid, name: fname }); } }}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" /></svg>
                        </span>
                      )}
                    </div>
                  );
                })}

                {/* Global add-multiple-rows (with freelancer selector) */}
                <button
                  type="button"
                  className="fc-tab-add-multiple"
                  onClick={() => setShowGlobalAutoGen(true)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6L12 3z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /></svg>
                  {t('ADD_MULTIPLE_ROWS') || 'Add multiple rows'}
                </button>
              </div>
            )}
            {/* Freelancer cards — hidden ones stay mounted (data preserved) */}
            {uniqueFreelancersForInvoice.map((friend: any, index: number) => {
              // console.log(`📋 [FREELANCER RENDER] Rendering freelancer at index ${index}:`, {
              //   userId: friend.userId,
              //   id: friend.id,
              //   name: friend.name,
              //   email: friend.email,
              //   fullData: friend
              // });
              
              // Filter timesheetData for THIS SPECIFIC freelancer only
              // This ensures each freelancer card ONLY receives their own timesheet data
              const freelancerUserId = (friend.userId || friend.id)?.toString();
              
              // Filter deduplicatedTimesheetData to only include entries for this specific freelancer
              const freelancerSpecificTimesheetData = deduplicatedTimesheetData?.filter((item: any) => {
                const itemUserId = item.userId?.toString();
                return itemUserId === freelancerUserId;
              }) || [];
              
              const isFcVisible = effectiveFreelancerTab === 'all' || effectiveFreelancerTab === freelancerUserId;
              return (
              <div key={friend.userId || friend.id} style={{ display: isFcVisible ? 'block' : 'none' }}>
              <FreelancerCardContainer
                friend={friend}
                index={index}
                expandedSections={expandedSections}
                toggleSection={toggleSection}
                register={register}
                setValue={setValue}
                watch={watch}
                errors={errors}
                trigger={trigger}
                isRegularEngagement={isRegularEngagement}
                currency={currency}
                isHourlyPrice={isHourlyPrice}
                isDailyPrice={isDailyPrice}
                isWeeklyPrice={isWeeklyPrice}
                isMonthlyPrice={isMonthlyPrice}
                deductionType={deductionType}
                hoursPerDayType={hoursPerDayType}
                workDates={workDates}
                showDates={showDates}
                weekOffDays={weekOffDays}
                serviceDescription={serviceDescription}
                serviceDescriptionError={serviceDescriptionError}
                isShowDatesDropdownOpen={isShowDatesDropdownOpen}
                isWeekOffDaysDropdownOpen={isWeekOffDaysDropdownOpen}
                showDatesDropdownRef={showDatesDropdownRef}
                weekOffDaysDropdownRef={weekOffDaysDropdownRef}
                showCalendar={showCalendar}
                activeFreelancerForCalendar={activeFreelancerForCalendar}
                calendarSelectedDatesByFreelancer={calendarSelectedDatesByFreelancer}
                tableRowsByFreelancer={tableRowsByFreelancer}
                serviceTableDataByFreelancer={serviceTableDataByFreelancer}
                deductionsByFreelancer={deductionsByFreelancer}
                pensionDataByFreelancer={pensionDataByFreelancer}
                holidayDataByFreelancer={holidayDataByFreelancer}
                copiedInvoiceData={copiedInvoiceData}
                removedCopiedWorkingHours={removedCopiedWorkingHours}
                calculatedSubAmount={calculatedSubAmount}
                isCalculateInvoiceLoading={isCalculateInvoiceLoading}
                serviceOptions={serviceOptions}
                showDatesOptions={showDatesOptions}
                showTooltipError={showTooltipError}
                handleInputChange={handleInputChange}
                handleCalendarOpen={handleCalendarOpen}
                handleCalendarClose={handleCalendarClose}
                handleDateSelect={handleDateSelect}
                onAutoGenerateItems={handleAutoGenerateItems}
                getModalDataByFreelancer={getModalDataByFreelancer}
                onRemoveFreelancer={handleRemoveFreelancer}
                setIsShowDatesDropdownOpen={setIsShowDatesDropdownOpen}
                setIsWeekOffDaysDropdownOpen={setIsWeekOffDaysDropdownOpen}
                setWeekOffDays={setWeekOffDays}
                setServiceDescription={setServiceDescription}
                setServiceDescriptionError={setServiceDescriptionError}
                handleRowChange={handleRowChange}
                handleRemoveRow={handleRemoveRow}
                handleAddWorkingHoursSection={handleAddWorkingHoursSection}
                setServiceTableDataByFreelancer={setServiceTableDataByFreelancer}
                handleOpenDeductionModal={handleOpenDeductionModal}
                setEditingDeductionData={setEditingDeductionData}
                setCurrentDeductionFreelancerId={setCurrentDeductionFreelancerId}
                setEditingDeductionIndex={setEditingDeductionIndex}
                setEditingCopiedWorkingHourIndex={setEditingCopiedWorkingHourIndex}
                setShowDeductionModal={setShowDeductionModal}
                timesheetData={freelancerSpecificTimesheetData}
                setRemovedCopiedWorkingHours={setRemovedCopiedWorkingHours}
                setDeductionsByFreelancer={setDeductionsByFreelancer}
                setPensionDataByFreelancer={setPensionDataByFreelancer}
                setHolidayDataByFreelancer={setHolidayDataByFreelancer}
                globalData={globalData}
                preferences={preferences}
                validateServiceDescriptionsRef={validateServiceDescriptionsRef}
                validateDatesRef={validateDatesRef}
                t={t}
                selectedFreelancersForInvoice={selectedFreelancersForInvoice}
                calculatedAmounts={calculatedAmounts}
                customerState={customerState}
                dispatch={dispatch}
              />
              </div>
              );
            })}
            </>
          )}

          {/* Express Payments Section — rendered into the Step 5 slot via portal.
              All logic/validation/modals remain here in Items. */}
          {expressSlot && createPortal(
          <ExpressPaymentSection
            selectedCustomer={selectedCustomer}
            selectedFreelancersForInvoice={selectedFreelancersForInvoice}
            register={register}
            watch={watch}
            setValue={setValue}
            errors={errors}
            isExpressCheckboxChecked={isExpressCheckboxChecked}
            isEditModeInvoice={isEditModeInvoice || false}
            handleExpressPaymentCheckbox={handleExpressPaymentCheckbox}
            handleExpressPaymentUncheckRequest={handleExpressPaymentUncheckRequest}
            handleExpressPaymentReadMore={handleExpressPaymentReadMore}
            isExpressPaymentAvailable={isExpressPaymentAvailable}
            getExpressPaymentMessage={getExpressPaymentMessage}
            isCalculateInvoiceLoading={isCalculateInvoiceLoading}
            calculatedSubAmount={calculatedSubAmount}
            currency={currency}
            language={i18n.language}
            vat={vat}
            vatAmount={vatAmount}
            isCalculationLoading={isCalculationLoading}
            handleShowCalculationBreakdown={handleShowCalculationBreakdown}
            t={t}
          />,
          expressSlot
          )}
        </div>
      </Card.Body>

      <InvoiceModals
        showDeductionModal={showDeductionModal}
        showCalculationModal={showCalculationModal}
        showExpressPaymentModal={showExpressPaymentModal}
        handleCloseDeductionModal={handleCloseDeductionModal}
        handleDeductionSubmit={handleDeductionSubmit}
        setShowCalculationModal={setShowCalculationModal}
        setCalculationBreakdownData={setCalculationBreakdownData}
        setShowExpressPaymentModal={setShowExpressPaymentModal}
        handleExpressPaymentModalConfirm={handleExpressPaymentModalConfirm}
        handleExpressPaymentModalReject={handleExpressPaymentModalReject}
        expressModalMode={expressModalMode}
        register={register}
        setValue={setValue}
        watch={watch}
        errors={errors}
        trigger={trigger}
        t={t}
        editingDeductionData={editingDeductionData}
        copiedInvoiceData={copiedInvoiceData}
        calculationBreakdownData={calculationBreakdownData}
        currency={currency}
        calculatedSubAmount={calculatedSubAmount}
        preferences={preferences}
      />

      {/* Global "Add multiple rows" with a freelancer selector (All = every freelancer) */}
      <AutoGenerateItemsModal
        isOpen={showGlobalAutoGen}
        onClose={() => setShowGlobalAutoGen(false)}
        freelancerId=""
        freelancerName=""
        onGenerate={() => {}}
        isRegularEngagement={isRegularEngagement}
        currency={currency}
        t={t}
        showDatesOptions={showDatesOptions}
        showFreelancerSelector={true}
        freelancerOptions={freelancerTargetOptions}
        initialFreelancerTarget={effectiveFreelancerTab}
        onGenerateForFreelancer={handleGlobalGenerate}
        existingDatesByFreelancer={freelancerExistingDates}
        onRemoveExistingDate={handleRemoveFreelancerDate}
        globalData={globalData}
        pensionData={pensionDataByFreelancer}
        holidayData={holidayDataByFreelancer}
        onPensionChange={setPensionDataByFreelancer}
        onHolidayChange={setHolidayDataByFreelancer}
        calculatedSubAmount={calculatedSubAmount}
        existingRowsByFreelancer={tableRowsByFreelancer}
      />

      {/* Remove-freelancer confirmation triggered from a tab's × */}
      {removeTabTarget && (
        <div
          className="modal-overlay"
          style={{ position: 'fixed', inset: 0, background: 'rgba(26,19,48,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100000, padding: '16px' }}
        >
          <div className="confirmation-modal fc-remove-modal" style={{ backgroundColor: '#fff', borderRadius: '8px', maxWidth: '590px', width: '90%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 16px' }}>
              <button className="fc-rm-close" onClick={() => setRemoveTabTarget(null)} aria-label="Close" style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5" stroke="#252525" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', padding: '24px 16px', alignItems: 'center' }}>
              {t('REMOVE_FREELANCER') || 'Remove Freelancer'}
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#000', textAlign: 'center', lineHeight: '18px' }}>
                {t('REMOVE_FREELANCER_TITLE') || 'Remove freelancer from this collective invoice?'}
              </h3>
              <div
                style={{ margin: 0, fontSize: '16px', fontWeight: 400, color: '#252B37', lineHeight: 1.4, textAlign: 'center', maxWidth: '476px' }}
                dangerouslySetInnerHTML={{
                  __html: (t('CONFIRM_REMOVE_FREELANCER_MESSAGE') || '')
                    .replaceAll('<strong>', '')
                    .replaceAll('</strong>', '')
                    .replace('{{name}}', `<strong style="font-weight:700;">${removeTabTarget.name}</strong>`)
                }}
              />
            </div>
            <div style={{ padding: '24px 16px', display: 'flex', justifyContent: 'center', gap: '16px' }}>
              <button className="fc-rm-cancel" onClick={() => setRemoveTabTarget(null)} style={{ padding: '10px 18px', fontSize: '14px', fontWeight: 700, borderRadius: '25px', border: '2px solid #6750B8', color: '#544196', backgroundColor: 'transparent', cursor: 'pointer', height: '40px' }}>
                {t('CANCEL_BUTTON') || 'Cancel'}
              </button>
              <button className="fc-rm-confirm" onClick={confirmRemoveTab} style={{ padding: '10px 18px', fontSize: '13px', fontWeight: 700, borderRadius: '25px', backgroundColor: '#6750B8', border: 'none', color: '#fff', cursor: 'pointer', height: '40px' }}>
                {t('REMOVE_FREELANCER_BUTTON') || 'Remove Freelancer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default ItemDetails;